/**
 * Get the Look — Section 1 drawer behavior
 *
 * Fixes addressed in this version:
 *
 * 1. SAFARI TOGGLE-BUTTON FALLBACK
 *    is="toggle-button" is a "customised built-in" custom element — Chrome/Firefox
 *    support it natively; Safari ignores it and leaves the button as a plain
 *    HTMLButtonElement. We detect this and add a direct click→drawer.open=true
 *    handler only when needed, leaving Chrome's own ToggleButton to handle it
 *    (so we don't double-toggle on the second click).
 *
 * 2. SIZE-PILL → VARIANT SYNC
 *    Radio values are now real variant IDs (Liquid fix: `where: look_size_opt_key`).
 *    The change handler copies the checked radio's value into the hidden #id input.
 *
 * 3. AJAX ADD-TO-CART
 *    Submits via /cart/add.js with sections:['mini-cart'] so we can pass the
 *    already-rendered mini-cart HTML back in the cart:refresh event, saving the
 *    CartDrawer from doing a second round-trip fetch.
 *    Drawer intentionally stays open so the shopper can add multiple items.
 *
 * 4. CART:REFRESH TARGET
 *    Must be dispatched on document.documentElement (the <html> element).
 *    CartDrawer and CartCount both listen via rootDelegate which is bound to
 *    document.documentElement — events dispatched on `document` never bubble
 *    up to document.documentElement.
 *
 * 5. BUTTON LOADING STATE
 *    Managed manually (no is="loader-button") to avoid the Safari path in
 *    LoaderButton that calls form.submit() directly, bypassing JS submit events.
 *
 * 6. RT SIZE CHART APP RE-TRIGGER
 *    The RT app typically scans for .product-form__option-name on DOMContentLoaded.
 *    Because our labels are inside a hidden drawer, the app may miss them.
 *    We dispatch a 'rt:reinit' synthetic event (common RT convention) and fall
 *    back to re-scanning via MutationObserver once on the first drawer open.
 */

(function () {
  'use strict';

  /**
   * Detect whether the browser actually upgrades customised built-in elements
   * (is="toggle-button"). Chrome/Firefox do; Safari does not.
   */
  function needsToggleButtonFallback() {
    var ToggleBtnCtor = window.customElements && customElements.get('toggle-button');
    if (!ToggleBtnCtor) return true; // theme.js not loaded yet — add fallback to be safe
    try {
      var testEl = document.createElement('button', { is: 'toggle-button' });
      return !(testEl instanceof ToggleBtnCtor);
    } catch (e) {
      return true;
    }
  }

  var safariMode = needsToggleButtonFallback();
  var rtReinitDispatched = false;

  /**
   * Wire up a single .get-the-look-drawer element.
   * Called once per drawer on DOMContentLoaded.
   */
  function initLookDrawer(drawerEl) {
    var drawerId = drawerEl.id;

    // ── 1. THUMBNAIL CLICK → OPEN DRAWER (Safari fallback) ─────────────────
    if (safariMode) {
      var triggers = document.querySelectorAll(
        '[data-get-the-look-trigger][aria-controls="' + drawerId + '"]'
      );
      triggers.forEach(function (btn) {
        btn.addEventListener('click', function () {
          drawerEl.open = true;
        });
      });
    }

    // Keep aria-expanded in sync on all trigger buttons whenever the drawer
    // opens or closes (important for both Chrome and Safari).
    document.addEventListener('openable-element:open', function (evt) {
      if (evt.target === drawerEl) {
        document.querySelectorAll('[aria-controls="' + drawerId + '"]').forEach(function (btn) {
          btn.setAttribute('aria-expanded', 'true');
        });
        maybeReinitRtApp(drawerEl);
      }
    });
    document.addEventListener('openable-element:close', function (evt) {
      if (evt.target === drawerEl) {
        document.querySelectorAll('[aria-controls="' + drawerId + '"]').forEach(function (btn) {
          btn.setAttribute('aria-expanded', 'false');
        });
      }
    });

    // ── 2. COLOR + SIZE → RESOLVE VARIANT ID ────────────────────────────────
    function parseVariants(row) {
      var node = row.querySelector('[data-look-variants]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return [];
      }
    }

    function optionIndexes(row) {
      var colorIndex = row.hasAttribute('data-color-option-index')
        ? parseInt(row.getAttribute('data-color-option-index'), 10)
        : -1;
      var sizeIndex = row.hasAttribute('data-size-option-index')
        ? parseInt(row.getAttribute('data-size-option-index'), 10)
        : -1;
      return { colorIndex: colorIndex, sizeIndex: sizeIndex };
    }

    function selectedLookColor(row) {
      var input = row.querySelector('[data-look-color-options] input:checked');
      return input ? input.value : null;
    }

    function selectedLookSize(row) {
      var input = row.querySelector('[data-look-size-options] input:checked');
      return input && !input.disabled ? input.value : null;
    }

    function optionValueAt(variant, index) {
      if (!variant || index < 0) return '';
      if (index === 0) return variant.option1 || (variant.options && variant.options[0]) || '';
      if (index === 1) return variant.option2 || (variant.options && variant.options[1]) || '';
      if (index === 2) return variant.option3 || (variant.options && variant.options[2]) || '';
      return (variant.options && variant.options[index]) || '';
    }

    function sameOption(a, b) {
      return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
    }

    function findAvailableLookVariant(variants, indexes, color, size) {
      var matches = function (requireAvailable, requireSize) {
        return variants.find(function (variant) {
          if (requireAvailable && !variant.available) return false;
          if (indexes.colorIndex >= 0 && color && !sameOption(optionValueAt(variant, indexes.colorIndex), color)) {
            return false;
          }
          if (requireSize && indexes.sizeIndex >= 0 && size && !sameOption(optionValueAt(variant, indexes.sizeIndex), size)) {
            return false;
          }
          return true;
        });
      };
      return matches(true, true) || matches(true, false) || matches(false, true) || matches(false, false);
    }

    function updateSizeAvailability(row, variants, indexes, color) {
      var firstAvailableRadio = null;
      row.querySelectorAll('[data-look-size-options] input').forEach(function (radio) {
        var sizeValue = radio.getAttribute('data-size-name') || radio.value;
        var hasStock = variants.some(function (variant) {
          if (!variant.available) return false;
          if (indexes.colorIndex >= 0) {
            if (!color || !sameOption(optionValueAt(variant, indexes.colorIndex), color)) return false;
          }
          if (indexes.sizeIndex >= 0 && !sameOption(optionValueAt(variant, indexes.sizeIndex), sizeValue)) {
            return false;
          }
          return true;
        });
        radio.disabled = !hasStock;
        var swatch = radio.closest('.block-swatch');
        if (swatch) {
          swatch.classList.toggle('is-disabled', !hasStock);
          swatch.classList.toggle('is-unavailable', !hasStock);
        }
        if (!hasStock) {
          radio.checked = false;
        } else if (!firstAvailableRadio) {
          firstAvailableRadio = radio;
        }
      });
      if (!row.querySelector('[data-look-size-options] input:checked') && firstAvailableRadio) {
        firstAvailableRadio.checked = true;
      }
    }

    function updateColorAvailability(row, variants, indexes) {
      row.querySelectorAll('[data-look-color-options] input').forEach(function (radio) {
        var hasStock = variants.some(function (variant) {
          if (!variant.available) return false;
          return sameOption(optionValueAt(variant, indexes.colorIndex), radio.value);
        });
        radio.disabled = !hasStock;
        var swatch = radio.closest('.color-swatch');
        if (swatch) {
          swatch.classList.toggle('is-disabled', !hasStock);
          swatch.classList.toggle('is-unavailable', !hasStock);
        }
      });
    }

    function syncLookRow(row) {
      var variants = parseVariants(row);
      var indexes = optionIndexes(row);
      var color = selectedLookColor(row) || (row.querySelector('[data-look-color-options] input:not(:disabled)') || {}).value || null;
      var sizeInput = row.querySelector('[data-look-size-options] input:checked');
      var size = sizeInput ? (sizeInput.getAttribute('data-size-name') || sizeInput.value) : null;
      var hiddenInput = row.querySelector('[data-look-variant-id]');
      var btn = row.querySelector('[type="submit"]');
      var img = row.querySelector('.get-the-look-drawer__image');
      var colorNameEl = row.querySelector('[data-look-color-name]');

      if (colorNameEl && color) {
        colorNameEl.textContent = color;
      }

      if (indexes.colorIndex >= 0) {
        updateColorAvailability(row, variants, indexes);
      }

      if (indexes.sizeIndex >= 0) {
        updateSizeAvailability(row, variants, indexes, color);
        sizeInput = row.querySelector('[data-look-size-options] input:checked');
        size = sizeInput ? (sizeInput.getAttribute('data-size-name') || sizeInput.value) : null;
      }

      var variant = findAvailableLookVariant(variants, indexes, color, size);

      if (hiddenInput && variant && variant.id) {
        hiddenInput.value = String(variant.id);
      }

      if (img && variant && variant.image) {
        img.src = variant.image;
        img.removeAttribute('srcset');
      }

      if (btn) {
        if (variant && variant.available) {
          btn.disabled = false;
          if (btn.textContent === 'Sold Out' || btn.textContent === 'Select a size') {
            btn.textContent = 'Add to Bag';
          }
        } else if (!size && indexes.sizeIndex >= 0) {
          btn.disabled = false;
        } else {
          btn.disabled = true;
          btn.textContent = 'Sold Out';
        }
      }
    }

    drawerEl.querySelectorAll('[data-look-product-id]').forEach(function (row) {
      syncLookRow(row);
    });

    drawerEl.addEventListener('change', function (evt) {
      if (!evt.target.closest('[data-look-color-options], [data-look-size-options]')) return;
      var row = evt.target.closest('[data-look-product-id]');
      if (row) syncLookRow(row);
    });
    drawerEl.addEventListener('click', function (evt) {
      if (evt.target.closest('.block-swatch.is-disabled, .color-swatch.is-disabled, .block-swatch.is-unavailable, .color-swatch.is-unavailable')) {
        evt.preventDefault();
        evt.stopPropagation();
      }
    });

    // ── 3. AJAX ADD-TO-CART (drawer stays open) ─────────────────────────────
    drawerEl.querySelectorAll('[data-look-add-form]').forEach(function (form) {
      form.addEventListener('submit', function (evt) {
        evt.preventDefault();

        var submitBtn = form.querySelector('button[type="submit"]');
        var variantInput = form.querySelector('[data-look-variant-id]');
        var variantId = variantInput ? variantInput.value : '';

        if (!variantId) {
          // No size selected — give visual feedback
          submitBtn.textContent = 'Select a size';
          setTimeout(function () {
            submitBtn.textContent = 'Add to Bag';
          }, 1500);
          return;
        }

        var originalText = submitBtn.textContent;
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
        submitBtn.textContent = 'Adding…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1, sections: ['mini-cart'] }),
        })
          .then(function (res) {
            if (!res.ok) {
              return res.json().then(function (err) { throw err; });
            }
            return res.json();
          })
          .then(function (addResponse) {
            submitBtn.textContent = 'Added ✓';

            // Fetch the current cart state so CartCount updates immediately.
            // Merge the already-rendered mini-cart HTML (from addResponse.sections)
            // into the cart data so CartDrawer doesn't need a second server round-trip.
            fetch('/cart.js')
              .then(function (r) { return r.json(); })
              .then(function (cartData) {
                if (addResponse.sections) {
                  cartData.sections = addResponse.sections;
                }
                // ── CRITICAL: dispatch on document.documentElement, not document ──
                // CartDrawer and CartCount listen via rootDelegate which is bound to
                // document.documentElement. Events on `document` never reach it.
                document.documentElement.dispatchEvent(
                  new CustomEvent('cart:refresh', {
                    bubbles: true,
                    detail: {
                      cart: cartData,
                      openMiniCart: false, // keep the GTL drawer open
                    },
                  })
                );
              })
              .catch(function () {
                // Even if the cart fetch fails, dispatch a bare cart:refresh so
                // CartDrawer/CartCount do their own fallback fetch.
                document.documentElement.dispatchEvent(
                  new CustomEvent('cart:refresh', { bubbles: true, detail: { openMiniCart: false } })
                );
              });

            setTimeout(function () {
              submitBtn.textContent = originalText;
              submitBtn.disabled = false;
              submitBtn.removeAttribute('aria-busy');
            }, 1500);
          })
          .catch(function (err) {
            console.error('Get the Look — add to cart failed:', err);
            var msg = (err && err.description) ? err.description : 'Could not add — try again';
            submitBtn.textContent = msg;
            setTimeout(function () {
              submitBtn.textContent = originalText;
              submitBtn.disabled = false;
              submitBtn.removeAttribute('aria-busy');
            }, 2000);
          });
      });
    });
  }

  /**
   * Attempt to re-trigger the RT: Size Chart app's scanner once the drawer
   * opens for the first time. Most RT app versions respond to one of:
   *   • A 'rt:reinit' CustomEvent on document
   *   • window.RTSizeChart.init()
   *   • Re-adding .product-form__option-name nodes to the DOM (MutationObserver)
   *
   * Because the app is closed-source we try all safe approaches.
   */
  function maybeReinitRtApp(drawerEl) {
    if (rtReinitDispatched) return;
    rtReinitDispatched = true;

    // Option A — common event name used by RT and similar apps
    document.dispatchEvent(new CustomEvent('rt:reinit', { bubbles: true }));

    // Option B — if the app exposes a global init function
    if (window.RTSizeChart && typeof window.RTSizeChart.init === 'function') {
      window.RTSizeChart.init();
    }

    // Option C — if the app uses a MutationObserver on the document, simply
    // touching (re-appending) the labels will fire it. We do this by moving the
    // option-info divs to themselves — a no-op that triggers the observer.
    drawerEl.querySelectorAll('.product-form__option-info').forEach(function (el) {
      el.parentNode.insertBefore(el, el.nextSibling);
    });
  }

  // ── INITIALISE ─────────────────────────────────────────────────────────────
  // The <script defer> tag ensures this runs after HTML parsing and after
  // theme.js (which defines custom elements), but before DOMContentLoaded.
  // Wrapping in DOMContentLoaded is still safe and harmless.
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.get-the-look-drawer').forEach(initLookDrawer);
  });
})();
